import React, { useState } from 'react';
import { Alert, Button, Input, Link } from '@heroui/react';
import { CheckCircle2 } from 'lucide-react';
import RegistrationApi from './RegistrationApi.js';
import { parseRegistrationError } from './parseRegistrationError.js';

const EMPTY_FIELDS = { email: '', password: '', passwordConfirmation: '' };

/**
 * RegistrationForm — the sign-up form, rendered by the standalone
 * `/authorization/registration/` page and embedded beside login on the
 * plan-purchase page.
 *
 * A 201 carries no token and no session: the account stays unconfirmed until
 * the emailed link is used. So this form never signs anyone in — it only ever
 * swaps itself for the "check your email" state.
 *
 * @param {{onSuccess?: () => void}} props `onSuccess` fires once, when a
 *   submission succeeds — the standalone page uses it to drop chrome that
 *   should not survive next to the success state, without this component
 *   having to know that chrome exists.
 */
export default function RegistrationForm({ onSuccess } = {}) {
	const [fields, setFields] = useState(EMPTY_FIELDS);
	const [fieldErrors, setFieldErrors] = useState({});
	const [generalError, setGeneralError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [registeredEmail, setRegisteredEmail] = useState(null);
	const [resend, setResend] = useState({ status: 'idle', message: null });

	const setField = (key, value) => setFields(current => ({ ...current, [key]: value }));

	const handleSubmit = async event => {
		event.preventDefault();
		setGeneralError(null);
		setFieldErrors({});
		setSubmitting(true);
		try {
			await RegistrationApi.register(fields);
			setRegisteredEmail(fields.email);
			onSuccess?.();
		} catch (err) {
			const parsed = parseRegistrationError(err);
			setFieldErrors(parsed.fieldErrors);
			setGeneralError(parsed.generalMessage);
		} finally {
			setSubmitting(false);
		}
	};

	const handleResend = async () => {
		setResend({ status: 'sending', message: null });
		try {
			const { data } = await RegistrationApi.resendConfirmation(registeredEmail);
			setResend({ status: 'sent', message: data?.message });
		} catch (err) {
			setResend({ status: 'failed', message: parseRegistrationError(err).generalMessage });
		}
	};

	if (registeredEmail) {
		return (
			<div className="flex flex-col items-center gap-4 py-2 text-center">
				<span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
					<CheckCircle2 className="h-6 w-6 text-success-600" aria-hidden="true" />
				</span>

				<div className="flex flex-col gap-1">
					<p className="m-0 text-[15px] font-semibold text-foreground">Check your email</p>
					<p className="m-0 text-sm text-default-500">
						We sent a confirmation link to{' '}
						<span className="font-medium text-foreground">{registeredEmail}</span>. Open it to
						activate your account.
					</p>
				</div>

				{/* Sibling of the message, never a child: a failed resend renders
				    danger chrome, which must not sit inside a success panel. */}
				{resend.status === 'sent' && (
					<Alert color="success" variant="flat" radius="sm">
						{resend.message}
					</Alert>
				)}
				{resend.status === 'failed' && (
					<Alert color="danger" variant="flat" radius="sm">
						{resend.message}
					</Alert>
				)}

				<p className="m-0 text-sm text-default-500">
					Didn&rsquo;t get it?{' '}
					<Link
						as="button"
						type="button"
						size="sm"
						className="text-primary"
						isDisabled={resend.status === 'sending'}
						onPress={handleResend}
					>
						{resend.status === 'sending' ? 'Sending…' : 'Resend the email'}
					</Link>
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-5">
			{generalError && (
				<Alert color="danger" variant="flat" radius="sm">
					{generalError}
				</Alert>
			)}

			<div className="flex flex-col gap-4">
				<Input
					name="email"
					type="email"
					label="Email"
					labelPlacement="outside-top"
					variant="bordered"
					radius="sm"
					autoComplete="username"
					maxLength={50}
					isRequired
					value={fields.email}
					onValueChange={v => setField('email', v)}
					isInvalid={Boolean(fieldErrors.email)}
					errorMessage={fieldErrors.email}
					description={fieldErrors.email ? undefined : 'This is also your username.'}
					classNames={{ inputWrapper: 'h-11' }}
				/>
				<Input
					name="password"
					type="password"
					label="Password"
					labelPlacement="outside-top"
					variant="bordered"
					radius="sm"
					autoComplete="new-password"
					maxLength={50}
					isRequired
					value={fields.password}
					onValueChange={v => setField('password', v)}
					isInvalid={Boolean(fieldErrors.password)}
					errorMessage={fieldErrors.password}
					description={fieldErrors.password ? undefined : 'At least 6 characters.'}
					classNames={{ inputWrapper: 'h-11' }}
				/>
				<Input
					name="repassword"
					type="password"
					label="Confirm password"
					labelPlacement="outside-top"
					variant="bordered"
					radius="sm"
					autoComplete="new-password"
					maxLength={50}
					isRequired
					value={fields.passwordConfirmation}
					onValueChange={v => setField('passwordConfirmation', v)}
					isInvalid={Boolean(fieldErrors.passwordConfirmation)}
					errorMessage={fieldErrors.passwordConfirmation}
					classNames={{ inputWrapper: 'h-11' }}
				/>
			</div>

			<Button
				type="submit"
				color="primary"
				radius="sm"
				className="h-11 font-medium"
				isLoading={submitting}
			>
				{submitting ? 'Creating account…' : 'Create account'}
			</Button>
		</form>
	);
}
